import { useEffect, useRef, useState } from "react";
import api from "../lib/api";

/**
 * VideoLockdownPlayer — D4 enforcement.
 * - First view: no skipping forward beyond highest watched seconds.
 * - Marks lesson completed at >= 97% playback.
 * - After first completion: free seek / fast-forward.
 * - Periodically posts progress to backend.
 *
 * Props:
 *   src           — video URL
 *   lessonId      — lesson id for progress tracking
 *   enforce       — boolean (course.enforce_video_lockdown)
 *   onCompleted   — callback fired the first time the video completes
 */
export default function VideoLockdownPlayer({ src, lessonId, enforce = true, onCompleted }) {
  const videoRef = useRef(null);
  const [maxWatched, setMaxWatched] = useState(0);
  const [completed, setCompleted] = useState(false);
  const [duration, setDuration] = useState(0);
  const [loadedProgress, setLoadedProgress] = useState(false);
  const lastSavedRef = useRef(0);

  // Load existing progress on mount
  useEffect(() => {
    if (!lessonId) { setLoadedProgress(true); return; }
    let alive = true;
    api.get(`/lessons/${lessonId}/video-progress`).then(r => {
      if (!alive) return;
      const p = r.data || {};
      setMaxWatched(p.seconds_watched || 0);
      setCompleted(!!p.completed);
      if (videoRef.current && p.last_position) {
        try { videoRef.current.currentTime = p.last_position; } catch (e) { /* noop */ }
      }
      setLoadedProgress(true);
    }).catch(() => setLoadedProgress(true));
    return () => { alive = false; };
  }, [lessonId]);

  // Throttled save (every 5s)
  const saveProgress = (force = false) => {
    if (!lessonId || !videoRef.current) return;
    const now = Date.now();
    if (!force && now - lastSavedRef.current < 5000) return;
    lastSavedRef.current = now;
    api.post(`/lessons/${lessonId}/video-progress`, {
      seconds_watched: maxWatched,
      duration_seconds: duration || videoRef.current.duration || 0,
      last_position: videoRef.current.currentTime || 0,
      completed,
    }).catch(() => {});
  };

  const handleTimeUpdate = (e) => {
    const v = e.target;
    const t = v.currentTime;
    if (t > maxWatched) setMaxWatched(t);
    saveProgress(false);
    // Completion at 97% of duration
    if (!completed && v.duration && t >= v.duration * 0.97) {
      setCompleted(true);
      saveProgress(true);
      onCompleted && onCompleted();
    }
  };

  const handleSeeking = (e) => {
    if (!enforce || completed) return; // free seeking once completed
    const v = e.target;
    // Allow tiny browser jitter (1s); block any forward seek beyond maxWatched + 1
    if (v.currentTime > maxWatched + 1) {
      v.currentTime = maxWatched;
    }
  };

  const handleLoadedMetadata = (e) => {
    setDuration(e.target.duration || 0);
  };

  const progressPct = duration ? Math.min(100, (maxWatched / duration) * 100) : 0;

  return (
    <div className="space-y-2" data-testid="video-lockdown-wrapper">
      {/* Lockdown banner */}
      {enforce && !completed && (
        <div className="bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900 flex items-center gap-2">
          <span className="inline-block w-2 h-2 bg-amber-500 rounded-full animate-pulse" />
          <span>First-watch mode — please watch the full video before continuing. Skipping forward is disabled.</span>
        </div>
      )}
      {enforce && completed && (
        <div className="bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs text-emerald-800">
          ✓ You've completed this lesson. You can now jump to any point freely.
        </div>
      )}
      <video
        ref={videoRef}
        src={src}
        controls
        controlsList={enforce && !completed ? "nodownload noplaybackrate" : "nodownload"}
        onContextMenu={(e) => e.preventDefault()}
        onTimeUpdate={handleTimeUpdate}
        onSeeking={handleSeeking}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={() => saveProgress(true)}
        onPause={() => saveProgress(true)}
        className="w-full bg-black"
        style={{ maxHeight: "60vh" }}
        data-testid="lockdown-video"
      />
      {/* Progress bar of "highest watched" — visible only during enforcement */}
      {enforce && !completed && (
        <div className="w-full bg-slate-100 h-1.5 overflow-hidden">
          <div className="bg-blue-700 h-full transition-all" style={{ width: `${progressPct}%` }} data-testid="watch-progress-bar" />
        </div>
      )}
      {!loadedProgress && <p className="text-xs text-slate-400">Loading progress…</p>}
    </div>
  );
}
